import React, { useState, useEffect } from 'react';
import { photoAPI } from '../../services/api';
import { Search, X, Tag as TagIcon, Download, Trash2, Plus } from 'lucide-react';
import TagManagementModal from '../Photos/TagManagementModal';

const PhotosTab = () => {
  const [photos, setPhotos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [error, setError] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    name: '',
    category: 'general',
    description: '',
    tags: '',
    isPermanent: false,
    selectedFiles: [],
  });
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [toast, setToast] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedPhotos, setSelectedPhotos] = useState(new Set());
  const [bulkMode, setBulkMode] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [showBulkCategoryModal, setShowBulkCategoryModal] = useState(false);
  const [selectedBulkCategory, setSelectedBulkCategory] = useState('');
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importUrl, setImportUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [generatingThumbnails, setGeneratingThumbnails] = useState(false);
  const [thumbnailProgress, setThumbnailProgress] = useState({ total: 0, processed: 0, success: 0, failed: 0 });

  // Fetch photos and categories on component mount
  useEffect(() => {
    fetchPhotos();
    fetchCategories();
    fetchTags();
  }, [selectedCategory, selectedTags]);

  // Handle ESC key to close fullscreen preview
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && previewPhoto) {
        setPreviewPhoto(null);
      }
    };

    if (previewPhoto) {
      window.addEventListener('keydown', handleEscKey);
      return () => {
        window.removeEventListener('keydown', handleEscKey);
      };
    }
  }, [previewPhoto]);

  // Fetch available tags
  const fetchTags = async () => {
    try {
      const response = await photoAPI.getAllTags();
      if (response.data.success) {
        setAvailableTags(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const fetchPhotos = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (selectedCategory !== 'all') {
        params.category = selectedCategory;
      }
      if (selectedTags.length > 0) {
        params.tags = selectedTags.join(',');
      }
      const response = await photoAPI.getAll(params);
      setPhotos(response.data.data || []);
    } catch (error) {
      console.error('Error fetching photos:', error);
      setError('Failed to load photos. Please try again.');
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await photoAPI.getCategories();
      setCategories(response.data.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    const files = uploadForm.selectedFiles.length > 0 
      ? uploadForm.selectedFiles 
      : (e.target.photo?.files ? Array.from(e.target.photo.files) : []);
    
    if (files.length === 0) {
      alert('Please select at least one photo to upload');
      return;
    }

    try {
      setUploading(true);
      const uploadedPhotos = [];
      const baseName = uploadForm.name || '';
      
      // Upload each file
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('photo', file);
        
        // Auto-append number to name if multiple files
        let photoName;
        if (files.length > 1) {
          // Multiple files: append number to the base name
          if (baseName && baseName.trim() !== '') {
            // Remove extension from base name if it has one
            const nameWithoutExt = baseName.replace(/\.[^/.]+$/, '');
            const fileExtension = file.name.split('.').pop();
            photoName = `${nameWithoutExt}${i + 1}.${fileExtension}`;
          } else {
            // No base name provided, use original filename with number
            const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
            const fileExtension = file.name.split('.').pop();
            photoName = `${fileNameWithoutExt}${i + 1}.${fileExtension}`;
          }
        } else {
          // Single file
          if (baseName && baseName.trim() !== '') {
            photoName = baseName;
          } else {
            photoName = file.name;
          }
        }
        
        formData.append('name', photoName);
        formData.append('category', uploadForm.category);
        formData.append('description', uploadForm.description);
        formData.append('tags', uploadForm.tags);
        formData.append('isPermanent', uploadForm.isPermanent);

        try {
          const response = await photoAPI.upload(formData);
          if (response.data.success) {
            uploadedPhotos.push(response.data.data);
          }
        } catch (fileError) {
          console.error(`Error uploading file ${i + 1}:`, fileError);
          // Continue with other files even if one fails
        }
      }

      // Add all uploaded photos to the list
      if (uploadedPhotos.length > 0) {
        setPhotos(prev => [...uploadedPhotos, ...prev]);
        setToast(`Successfully uploaded ${uploadedPhotos.length} photo${uploadedPhotos.length !== 1 ? 's' : ''}`);
        setTimeout(() => setToast(''), 3000);
      }
      
      setShowUploadModal(false);
      setUploadForm({ name: '', category: 'general', description: '', tags: '', isPermanent: false, selectedFiles: [] });
      e.target.reset();
    } catch (error) {
      console.error('Error uploading photos:', error);
      alert('Error uploading photos. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
  };

  const closePreview = () => setPreviewPhoto(null);

  const handleCopyUrl = async (photo) => {
    try {
      const absoluteUrl = `${window.location.origin}${photo.storageUrl || photo.url}`;
      await navigator.clipboard.writeText(absoluteUrl);
      setToast('Image URL copied to clipboard');
      setTimeout(() => setToast(''), 2000);
    } catch (e) {
      console.error('Failed to copy URL', e);
    }
  };

  const handleDownload = async (photo) => {
    try {
      const url = photo.storageUrl || photo.url;
      const res = await fetch(url);
      const blob = await res.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      const filename = photo.originalFilename || photo.name || 'photo';
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (e) {
      console.error('Failed to download image', e);
    }
  };

  const handleDeletePhoto = async (photo) => {
    if (!window.confirm('Move photo to trash?')) return
    try {
      await photoAPI.delete(photo.id)
      setPhotos((prev) => prev.filter((p) => p.id !== photo.id))
      if (previewPhoto && previewPhoto.id === photo.id) {
        closePreview();
      }
    } catch (e) {
      console.error('Failed to delete photo', e)
    }
  }

  const handleBulkCategoryChange = async () => {
    if (selectedPhotos.size === 0 || !selectedBulkCategory) return;
    
    setBulkActionLoading(true);
    try {
      await photoAPI.bulkUpdateCategory(Array.from(selectedPhotos), selectedBulkCategory, null);
      setShowBulkCategoryModal(false);
      setSelectedBulkCategory('');
      setSelectedPhotos(new Set());
      setBulkMode(false);
      fetchPhotos();
      setToast('Category updated successfully');
      setTimeout(() => setToast(''), 2000);
    } catch (error) {
      console.error('Error updating category:', error);
      setToast('Failed to update category');
      setTimeout(() => setToast(''), 2000);
    } finally {
      setBulkActionLoading(false);
    }
  }

  const handleUpdatePhotoCategory = async (photoId, categoryId) => {
    try {
      await photoAPI.update(photoId, { category_id: categoryId });
      fetchPhotos();
      if (previewPhoto && previewPhoto.id === photoId) {
        const updatedPhoto = photos.find(p => p.id === photoId);
        if (updatedPhoto) {
          setPreviewPhoto({ ...updatedPhoto, category_id: categoryId });
        }
      }
      setToast('Category updated successfully');
      setTimeout(() => setToast(''), 2000);
    } catch (error) {
      console.error('Error updating category:', error);
      setToast('Failed to update category');
      setTimeout(() => setToast(''), 2000);
    }
  }

  const handleImportFromUrl = async (e) => {
    e.preventDefault();
    if (!importUrl.trim()) {
      setToast('Please enter a valid URL');
      setTimeout(() => setToast(''), 2000);
      return;
    }

    setImporting(true);
    try {
      const response = await photoAPI.importFromUrl({
        url: importUrl.trim(),
        name: uploadForm.name || '',
        category: uploadForm.category || 'general',
        description: uploadForm.description || '',
        tags: uploadForm.tags || '',
        isPermanent: uploadForm.isPermanent || false,
      });

      if (response.data.success) {
        setPhotos(prev => [response.data.data, ...prev]);
        setShowImportModal(false);
        setImportUrl('');
        setUploadForm({ name: '', category: 'general', description: '', tags: '', isPermanent: false, selectedFiles: [] });
        setToast('Image imported successfully');
        setTimeout(() => setToast(''), 2000);
      }
    } catch (error) {
      console.error('Error importing image:', error);
      setToast(error.response?.data?.message || 'Failed to import image. Please check the URL and try again.');
      setTimeout(() => setToast(''), 3000);
    } finally {
      setImporting(false);
    }
  }

  const handleGenerateThumbnails = async () => {
    if (!window.confirm('This will generate thumbnails for all photos without thumbnails. This may take a while. Continue?')) {
      return;
    }

    setGeneratingThumbnails(true);
    setThumbnailProgress({ total: 0, processed: 0, success: 0, failed: 0 });
    
    try {
      // First, get all photos without thumbnails
      const response = await photoAPI.getPhotosWithoutThumbnails({ page: 1, limit: 1000 });
      const photosWithoutThumbnails = response.data.data || [];
      
      if (photosWithoutThumbnails.length === 0) {
        setToast('No photos without thumbnails found');
        setTimeout(() => setToast(''), 2000);
        setGeneratingThumbnails(false);
        return;
      }

      setThumbnailProgress({ 
        total: photosWithoutThumbnails.length, 
        processed: 0, 
        success: 0, 
        failed: 0 
      });

      // Generate thumbnails one by one
      let successCount = 0;
      let failedCount = 0;

      for (let i = 0; i < photosWithoutThumbnails.length; i++) {
        const photo = photosWithoutThumbnails[i];
        try {
          await photoAPI.generateThumbnail(photo.id);
          successCount++;
        } catch (error) {
          console.error(`Failed to generate thumbnail for photo ${photo.id}:`, error);
          failedCount++;
        }
        
        setThumbnailProgress({ 
          total: photosWithoutThumbnails.length, 
          processed: i + 1, 
          success: successCount, 
          failed: failedCount 
        });
      }

      // Refresh photos list
      fetchPhotos();
      
      setToast(`Thumbnail generation complete: ${successCount} succeeded, ${failedCount} failed`);
      setTimeout(() => setToast(''), 5000);
    } catch (error) {
      console.error('Error generating thumbnails:', error);
      setToast('Failed to generate thumbnails. Please try again.');
      setTimeout(() => setToast(''), 3000);
    } finally {
      setGeneratingThumbnails(false);
      setThumbnailProgress({ total: 0, processed: 0, success: 0, failed: 0 });
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Photos</h2>
          <p className="text-gray-600">Browse and upload photos for your designs</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowUploadModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            <span>Upload Photo</span>
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
            </svg>
            <span>Import from URL</span>
          </button>
          <button
            onClick={handleGenerateThumbnails}
            disabled={generatingThumbnails}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Auto generate thumbnails for photos without thumbnails"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
            </svg>
            <span>{generatingThumbnails ? 'Generating...' : 'Auto Generate Thumbnails'}</span>
          </button>
        </div>
      </div>

      {/* Category Chips */}
      <div className="overflow-x-auto mb-4">
        <div className="flex gap-2 pb-2" style={{ minWidth: 'max-content' }}>
          <button
            onClick={() => handleCategorySelect('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
              selectedCategory === 'all'
                ? 'bg-blue-100 text-blue-700 border border-blue-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All Photos
          </button>
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => handleCategorySelect(category.slug)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                selectedCategory === category.slug
                  ? 'bg-blue-100 text-blue-700 border border-blue-200'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>
      </div>

      {/* Tag Search and Filter */}
      <div className="mb-4 space-y-2">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by tags..."
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={() => setBulkMode(!bulkMode)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              bulkMode
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {bulkMode ? 'Cancel Selection' : 'Select Multiple'}
          </button>
        </div>
        
        {/* Selected Tags Filter Chips */}
        {selectedTags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {selectedTags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm flex items-center gap-2"
              >
                <TagIcon className="w-3 h-3" />
                {tag}
                <button
                  onClick={() => setSelectedTags(selectedTags.filter(t => t !== tag))}
                  className="hover:text-blue-900"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Popular Tags */}
        {availableTags.length > 0 && tagFilter === '' && selectedTags.length === 0 && (
          <div className="flex flex-wrap gap-2">
            <span className="text-sm text-gray-600">Popular tags:</span>
            {availableTags.slice(0, 10).map((tag) => (
              <button
                key={tag.id}
                onClick={() => {
                  if (!selectedTags.includes(tag.name)) {
                    setSelectedTags([...selectedTags, tag.name]);
                  }
                }}
                className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-gray-200 transition-colors flex items-center gap-1"
              >
                <TagIcon className="w-3 h-3" />
                {tag.name}
                <span className="text-xs text-gray-500">({tag.usageCount || 0})</span>
              </button>
            ))}
          </div>
        )}

        {/* Tag Suggestions based on search */}
        {tagFilter && availableTags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {availableTags
              .filter(tag => tag.name.toLowerCase().includes(tagFilter.toLowerCase()))
              .slice(0, 10)
              .map((tag) => (
                <button
                  key={tag.id}
                  onClick={() => {
                    if (!selectedTags.includes(tag.name)) {
                      setSelectedTags([...selectedTags, tag.name]);
                      setTagFilter('');
                    }
                  }}
                  className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-gray-200 transition-colors flex items-center gap-1"
                >
                  <TagIcon className="w-3 h-3" />
                  {tag.name}
                  <span className="text-xs text-gray-500">({tag.usageCount || 0})</span>
                </button>
              ))}
          </div>
        )}
      </div>

      {/* Bulk Action Toolbar */}
      {bulkMode && selectedPhotos.size > 0 && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
          <span className="text-sm font-medium text-blue-900">
            {selectedPhotos.size} photo{selectedPhotos.size !== 1 ? 's' : ''} selected
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedPhotos(new Set())}
              className="px-3 py-1 text-sm text-gray-700 hover:bg-gray-200 rounded"
            >
              Clear
            </button>
            <button
              onClick={() => setShowTagModal(true)}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1"
            >
              <TagIcon className="w-3 h-3" />
              Manage Tags
            </button>
            <button
              onClick={() => setShowBulkCategoryModal(true)}
              className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
            >
              Change Category
            </button>
          </div>
        </div>
      )}

      {/* Photos Grid */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {[...Array(12)].map((_, i) => (
            <div key={i} className="aspect-square bg-gray-200 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : photos.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {photos.map((photo) => (
            <div
              key={photo.id}
              className={`group relative aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-pointer hover:shadow-lg transition-shadow ${
                selectedPhotos.has(photo.id) ? 'ring-4 ring-blue-500' : ''
              }`}
              onClick={() => {
                if (!bulkMode) {
                  setPreviewPhoto(photo);
                }
              }}
            >
              {/* Bulk Selection Checkbox */}
              {bulkMode && (
                <div className="absolute top-2 left-2 z-10" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedPhotos.has(photo.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      const newSelected = new Set(selectedPhotos);
                      if (e.target.checked) {
                        newSelected.add(photo.id);
                      } else {
                        newSelected.delete(photo.id);
                      }
                      setSelectedPhotos(newSelected);
                    }}
                    className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                  />
                </div>
              )}
              <img
                src={photo.thumbnailUrl || photo.storageUrl || photo.url}
                alt={photo.name || photo.originalName || 'photo'}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-opacity flex flex-col justify-between">
                <div className="flex justify-between p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  {/* Storage Type Indicator and Tag Count */}
                  <div className="flex items-center gap-2">
                    {photo.storageType === 'spaces' ? (
                      <span className="px-2 py-1 bg-blue-500 text-white text-xs rounded-md">
                        Spaces
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-gray-500 text-white text-xs rounded-md">
                        Local
                      </span>
                    )}
                    {/* Tag Count Badge */}
                    {(photo.tagCount > 0 || (photo.tags && photo.tags.length > 0)) && (
                      <span className="px-2 py-1 bg-purple-500 text-white text-xs rounded-md flex items-center gap-1">
                        <TagIcon className="w-3 h-3" />
                        {photo.tagCount || (photo.tags ? photo.tags.length : 0)}
                      </span>
                    )}
                  </div>
                  
                  {/* Action Buttons - Only Tag button on hover */}
                  <div className="flex space-x-1">
                    {/* Add Tag button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedPhotos(new Set([photo.id]));
                        setShowTagModal(true);
                      }}
                      className="p-2 rounded-md bg-white bg-opacity-90 hover:bg-opacity-100 text-blue-600"
                      title="Add Tag"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                    {/* Copy URL */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopyUrl(photo);
                      }}
                      className="p-2 rounded-md bg-white bg-opacity-90 hover:bg-opacity-100 text-gray-700"
                      title="Copy URL"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M8 2a2 2 0 00-2 2v8a2 2 0 002 2h2v2H8a4 4 0 01-4-4V4a4 4 0 014-4h6a4 4 0 014 4v2h-2V4a2 2 0 00-2-2H8zm4 6a2 2 0 00-2 2v6a2 2 0 002 2h6a2 2 0 002-2v-6a2 2 0 00-2-2h-6zm6 2h-6v6h6V10z" clipRule="evenodd"/></svg>
                    </button>
                  </div>
                </div>
                <div className="p-3 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                  <p className="text-sm font-medium truncate">{photo.name}</p>
                  <p className="text-xs text-gray-300 mb-2">{photo.width} × {photo.height}</p>
                  {/* Tag Chips */}
                  {photo.tags && photo.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {photo.tags.slice(0, 3).map((tag, idx) => (
                        <span
                          key={idx}
                          className="px-1.5 py-0.5 bg-white/20 backdrop-blur-sm text-white text-xs rounded"
                        >
                          {tag}
                        </span>
                      ))}
                      {photo.tags.length > 3 && (
                        <span className="px-1.5 py-0.5 bg-white/20 backdrop-blur-sm text-white text-xs rounded">
                          +{photo.tags.length - 3}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <svg className="w-12 h-12 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No photos found</h3>
          <p className="text-gray-600 mb-4">Upload your first photo to get started</p>
          <button
            onClick={() => setShowUploadModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Upload Photo
          </button>
        </div>
      )}

      {/* Import from URL Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Import from URL</h3>
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setImportUrl('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleImportFromUrl} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Image URL <span className="text-red-500">*</span>
                </label>
                <input
                  type="url"
                  value={importUrl}
                  onChange={(e) => setImportUrl(e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter the direct URL to an image file (jpg, png, gif, webp, etc.)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  value={uploadForm.name}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Photo name (optional)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select
                  value={uploadForm.category}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="general">General</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.slug}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Photo description (optional)"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tags
                </label>
                <input
                  type="text"
                  value={uploadForm.tags}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, tags: e.target.value }))}
                  placeholder="Comma-separated tags (optional)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Storage Type
                </label>
                <select
                  value={uploadForm.isPermanent ? 'permanent' : 'temporary'}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, isPermanent: e.target.value === 'permanent' }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="temporary">Temporary Storage (Local)</option>
                  <option value="permanent">Permanent Storage (DigitalOcean Spaces)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {uploadForm.isPermanent 
                    ? 'Permanent storage: Image will be stored in DigitalOcean Spaces for long-term access and CDN delivery.'
                    : 'Temporary storage: Image will be stored locally for quick access during editing.'
                  }
                </p>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowImportModal(false);
                    setImportUrl('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={importing || !importUrl.trim()}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {importing ? 'Importing...' : 'Import Image'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Upload Photo</h3>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadForm(prev => ({ ...prev, selectedFiles: [] }));
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Photo{uploadForm.selectedFiles?.length > 0 ? ` (${uploadForm.selectedFiles.length} selected)` : ''}
                </label>
                <input
                  type="file"
                  name="photo"
                  accept="image/*"
                  multiple
                  required
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    setUploadForm(prev => ({ ...prev, selectedFiles: files }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {uploadForm.selectedFiles && uploadForm.selectedFiles.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {uploadForm.selectedFiles.length} file{uploadForm.selectedFiles.length !== 1 ? 's' : ''} selected. Names will be auto-numbered.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  value={uploadForm.name}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Photo name (optional)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select
                  value={uploadForm.category}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="general">General</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.slug}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Photo description (optional)"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tags
                </label>
                <input
                  type="text"
                  value={uploadForm.tags}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, tags: e.target.value }))}
                  placeholder="Comma-separated tags (optional)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Storage Type
                </label>
                <select
                  value={uploadForm.isPermanent ? 'permanent' : 'temporary'}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, isPermanent: e.target.value === 'permanent' }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="temporary">Temporary Storage (Local)</option>
                  <option value="permanent">Permanent Storage (DigitalOcean Spaces)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {uploadForm.isPermanent 
                    ? 'Permanent storage: Image will be stored in DigitalOcean Spaces for long-term access and CDN delivery.'
                    : 'Temporary storage: Image will be stored locally for quick access during editing.'
                  }
                </p>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowUploadModal(false);
                    setUploadForm(prev => ({ ...prev, selectedFiles: [] }));
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Fullscreen Preview Modal */}
      {previewPhoto && (
        <div className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center p-4">
          <div className="relative max-w-6xl w-full h-full flex flex-col">
            {/* Header with actions */}
            <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
              {/* Add Tag */}
              <button
                onClick={() => {
                  setSelectedPhotos(new Set([previewPhoto.id]));
                  setShowTagModal(true);
                }}
                className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-lg backdrop-blur-sm transition-colors"
                title="Add Tag"
              >
                <TagIcon className="w-5 h-5" />
              </button>
              {/* Download */}
              <button
                onClick={() => handleDownload(previewPhoto)}
                className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-lg backdrop-blur-sm transition-colors"
                title="Download"
              >
                <Download className="w-5 h-5" />
              </button>
              {/* Delete */}
              <button
                onClick={() => handleDeletePhoto(previewPhoto)}
                className="p-2 bg-white/10 hover:bg-white/20 text-red-400 rounded-lg backdrop-blur-sm transition-colors"
                title="Delete"
              >
                <Trash2 className="w-5 h-5" />
              </button>
              {/* Close */}
              <button
                onClick={closePreview}
                className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-lg backdrop-blur-sm transition-colors"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Image */}
            <div className="flex-1 flex items-center justify-center overflow-hidden">
              <img
                src={previewPhoto.storageUrl || previewPhoto.url}
                alt={previewPhoto.name || previewPhoto.originalName || 'photo'}
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              />
            </div>

            {/* Footer with info and category selector */}
            <div className="flex items-center justify-between text-white">
              <div className="flex-1">
                <div className="text-lg font-medium truncate">{previewPhoto.name || previewPhoto.originalName}</div>
                <div className="text-sm text-gray-300">{previewPhoto.width} × {previewPhoto.height}</div>
                {/* Tags */}
                {previewPhoto.tags && previewPhoto.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {previewPhoto.tags.map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-white/20 backdrop-blur-sm text-white text-xs rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              {/* Category Selector */}
              <div className="ml-4">
                <label className="block text-xs text-gray-300 mb-1">Category</label>
                <select
                  value={previewPhoto.category_id || ''}
                  onChange={(e) => handleUpdatePhotoCategory(previewPhoto.id, e.target.value || null)}
                  className="px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg backdrop-blur-sm border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="">None</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id} className="bg-gray-800">
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tag Management Modal */}
      <TagManagementModal
        isOpen={showTagModal}
        onClose={() => {
          setShowTagModal(false);
          if (bulkMode) {
            // Keep selection in bulk mode
          } else {
            setSelectedPhotos(new Set());
            setBulkMode(false);
          }
        }}
        photoIds={Array.from(selectedPhotos)}
        onSuccess={() => {
          fetchPhotos();
          if (previewPhoto) {
            // Refresh preview photo if it's in the selection
            if (selectedPhotos.has(previewPhoto.id)) {
              fetchPhotos().then(() => {
                const updated = photos.find(p => p.id === previewPhoto.id);
                if (updated) setPreviewPhoto(updated);
              });
            }
          }
          if (!bulkMode) {
            setSelectedPhotos(new Set());
          }
        }}
      />

      {/* Bulk Category Change Modal */}
      {showBulkCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                Change Category ({selectedPhotos.size} photo{selectedPhotos.size !== 1 ? 's' : ''})
              </h3>
              <button
                onClick={() => {
                  setShowBulkCategoryModal(false);
                  setSelectedBulkCategory('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Category
              </label>
              <select
                value={selectedBulkCategory}
                onChange={(e) => setSelectedBulkCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">None</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowBulkCategoryModal(false);
                  setSelectedBulkCategory('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleBulkCategoryChange}
                disabled={bulkActionLoading || !selectedBulkCategory}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {bulkActionLoading ? 'Updating...' : 'Update Category'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Thumbnail Generation Progress */}
      {generatingThumbnails && thumbnailProgress.total > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-white border border-gray-300 rounded-lg shadow-lg p-4 min-w-[300px]">
          <div className="mb-2">
            <div className="flex justify-between text-sm text-gray-700 mb-1">
              <span>Generating thumbnails...</span>
              <span>{thumbnailProgress.processed} / {thumbnailProgress.total}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(thumbnailProgress.processed / thumbnailProgress.total) * 100}%` }}
              />
            </div>
          </div>
          <div className="text-xs text-gray-600">
            <span className="text-green-600">✓ {thumbnailProgress.success} succeeded</span>
            {' '}
            <span className="text-red-600">✗ {thumbnailProgress.failed} failed</span>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white px-4 py-2 rounded-full shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
};

export default PhotosTab;
